import React from "react";
import styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { setActiveAssistant } from "../../../store/slices/assistantSlice";
import { InputFile } from "../../InputFile/InputFile";
import Switcher from "../../../ui/Switcher/Switcher";
import {
  deleteAssistantFile,
  postAddAssistantFiles,
  teachAssistant,
} from "../../../utils/api/actions/assistant";
import FileCard from "../../FileCard/FileCard";
import Button from "../../../ui/Button/Button";
import { GoogleDriveIcon } from "../../../assets/icons/GoogleDriveIcon";
import { hasJsonFile } from "../../../utils/methods/hasJSON";
import { ModalAddGoogleDrive } from "../../ModalAddGoogleDrive/ModalAddGoogleDrive";
import { errorToast } from "../../../utils/methods/toast";
import { ClipLoader } from "react-spinners";
import addFilesAssistant from "../../../utils/methods/addFilesAssistant";

export const Files = () => {
  const dispatch = useDispatch();

  const id =
    useSelector((state) => state.user?.data?.id) ||
    localStorage.getItem("@storage_User_Id");

  const activeAssistant = useSelector(
    (state) => state.assistant.activeAssistant
  );

  const [visibleGoogleDriveModal, setVisibleGoogleDriveModal] =
    React.useState(false);
  const [loadingFiles, setLoadingFiles] = React.useState(false);

  const driveFile = activeAssistant?.driveFile;
  const filesList = activeAssistant?.files || [];
  const hasValidFiles = filesList.some((file) => !!file.id);

  const handleOpenModalGoogleDrive = () => {
    setVisibleGoogleDriveModal(true);
  };

  const handleCloseModalGoogleDrive = () => {
    setVisibleGoogleDriveModal(false);
  };

  const toggleObserver = (bool) => {
    if (activeAssistant) {
      const updateAssistant = {
        ...activeAssistant,
        settings: { ...activeAssistant.settings, useFiles: bool },
      };

      dispatch(setActiveAssistant(updateAssistant));
    }
  };

  const handleDeleteFile = async (index, id) => {
    try {
      if (id) {
        // Если у файла есть id, значит он уже на сервере
        const result = await dispatch(deleteAssistantFile(id));

        if (result.success) {
          const updatedAssistant = {
            ...activeAssistant,
            files: activeAssistant.files.filter((_, i) => i !== index),
          };
          dispatch(setActiveAssistant(updatedAssistant));
        } else {
          errorToast(result.message || "Неизвестная ошибка");
        }
      } else if (driveFile && index === -1) {
        // Если это Google Drive файл
        dispatch(
          setActiveAssistant({
            ...activeAssistant,
            driveFile: null,
          })
        );
      } else {
        // Если это локальный файл
        const updatedAssistant = {
          ...activeAssistant,
          files: activeAssistant.files.filter((_, i) => i !== index),
        };
        dispatch(setActiveAssistant(updatedAssistant));
      }
    } catch (error) {
      errorToast("При удалении произошла ошибка");
    }
  };

  const handleTeachAssistant = () => {
    let data = {
      assistantId: activeAssistant?.id,
      userId: id,
    };
    dispatch(teachAssistant(data));
  };

  const handleSetDriveFile = (file) => {
    const updatedAssistant = {
      ...activeAssistant,
      driveFile: file,
    };
    dispatch(setActiveAssistant(updatedAssistant));
  };

  const handleSendFiles = async () => {
    const hasFiles = filesList && filesList.length > 0;
    const hasDriveFile = !!activeAssistant?.driveFile;

    if (!hasFiles && !hasDriveFile) {
      errorToast("Вы не выбрали ни файлов, ни файла из Google Диска.");
      return;
    }

    try {
      const requests = addFilesAssistant(
        activeAssistant,
        hasFiles ? filesList : [], // если нет файлов — передаём пустой массив
        hasDriveFile ? activeAssistant.driveFile : null
      );

      if (requests.length === 0) {
        errorToast("Нет данных для отправки.");
        return;
      }

      setLoadingFiles(true);
      for (const req of requests) {
        await dispatch(postAddAssistantFiles(req, setLoadingFiles));
      }
      setLoadingFiles(false);
    } catch (error) {
      setLoadingFiles(false);
      errorToast("Ошибка при отправке файлов: " + error.message);
    }
  };

  return (
    <div className={styles.container}>
      <ModalAddGoogleDrive
        visible={visibleGoogleDriveModal && !activeAssistant?.driveFile}
        handleClose={handleCloseModalGoogleDrive}
        driveFile={activeAssistant?.driveFile}
        setDriveFile={handleSetDriveFile}
      />
      <div className={styles.toggleContainer}>
        <div className={styles.toggleWrapper}>
          <Switcher
            bool={activeAssistant ? activeAssistant?.settings?.useFiles : false}
            onToggle={toggleObserver}
          />
          <label className={styles.label}>Использовать файлы</label>
        </div>
        <small className={styles.smallText}>
          При включенной опции, Вы сможете загружать файлы собственной базы
          знаний. Ассистент будет использовать ее для обучения
        </small>
      </div>
      {activeAssistant?.settings?.useFiles && (
        <>
          <div className={styles.fileUploadContainer}>
            {activeAssistant?.settings?.useFiles && (
              <div className={styles.left}>
                {hasValidFiles && (
                  <Button
                    className={styles.teachButton}
                    title={
                      activeAssistant?.status === "not_teached"
                        ? "Обучить"
                        : "Дообучить"
                    }
                    onClick={handleTeachAssistant}
                    disabled={activeAssistant?.status === "teaching"}
                  />
                )}
                <Button
                  className={styles.sendButton}
                  title={loadingFiles ? "Загрузка" : "Отправить"}
                  disabled={!driveFile && filesList.length === 0}
                  leftIcon={
                    loadingFiles && <ClipLoader size={12} color="white" />
                  }
                  onClick={handleSendFiles}
                />
                {!driveFile && !hasJsonFile(filesList) && (
                  <Button
                    leftIcon={<GoogleDriveIcon size={30} fill={"#000"} />}
                    className={styles.googleDrive}
                    titleClassName={styles.googleDriveTitle}
                    title={"Загрузить из Google Диск"}
                    onClick={handleOpenModalGoogleDrive}
                  />
                )}
              </div>
            )}
            <div className={styles.right}>
              <InputFile assistant={activeAssistant} />
            </div>
          </div>

          <div className={styles.filesWrapper}>
            {driveFile && (
              <FileCard
                name={driveFile.name}
                size={driveFile.size}
                onDelete={() => handleDeleteFile(-1)} // Используем -1 для Google Drive файла
              />
            )}
            {filesList.map((file, index) => (
              <FileCard
                key={index}
                name={file.name}
                size={file.size}
                onDelete={() => handleDeleteFile(index, file.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
